/**
 * resolveActivityLabel — render a backend "structured activity label"
 * (`titleKey` + `titleValues`) into a localized string.
 *
 * The Tonnie backend never ships finished English prose for activity / timeline
 * / miles feeds. It returns an i18n `titleKey` plus `titleValues`, where a value
 * of the form `__t:<key>__` is itself a nested i18n key (e.g. a catalog name or
 * pipeline-stage name) that must be translated before interpolation. This
 * helper does that resolution in one place so every feed across the three apps
 * renders identically.
 *
 * @example
 *   resolveActivityLabel(t, ev.titleKey, ev.titleValues, ev.title)
 *   // titleKey:   "activity.task.completed"            → "Completed: {{label}}"
 *   // titleValues:{ label: "__t:catalog.stamps.x.name__" } → label resolved via t()
 */

type TFn = (key: string, options?: Record<string, unknown>) => string;

const NESTED_KEY = /^__t:(.+)__$/;

export function resolveActivityLabel(
  t: TFn,
  titleKey: string | undefined | null,
  titleValues?: Record<string, string | number> | null,
  fallback?: string | null
): string {
  if (!titleKey) return fallback ?? '';

  const resolved: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(titleValues ?? {})) {
    if (typeof v === 'string') {
      const m = NESTED_KEY.exec(v);
      // Resolve nested key; fall back to the bare code if the key is missing.
      resolved[k] = m ? t(m[1]) : v;
    } else {
      resolved[k] = v;
    }
  }

  return t(titleKey, { ...resolved });
}
